import React from 'react';
import { motion } from 'framer-motion';
import { ExternalLink, Zap, DollarSign, Play, Star, Shield, ArrowRight } from 'lucide-react';

/**
 * =====================================
 * CTA BANNER LIBRARY
 * =====================================
 * Reusable CTA components for use across the site
 * All templates follow the Elite UI design system
 * =====================================
 */

/**
 * CTA1 - Primary Premium Banner
 * Use for: Main conversion CTAs, hero sections, pillar pages
 * Style: Glass morphism with primary accent, prominent button
 */
export const CTA1 = ({
    headline = "Save $2,000+ Per Year - Switch from Cable Today",
    subtext = "Same channels, same sports, 90% cheaper than cable.",
    buttonText = "Start Free Trial",
    buttonUrl = "https://xtreamcube.shop/freetrialiptv/",
    icon: Icon = DollarSign,
    showIcon = true
}) => (
    <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative overflow-hidden rounded-2xl p-8 md:p-10"
        style={{
            background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(6, 78, 59, 0.3) 50%, rgba(0, 0, 0, 0.4) 100%)',
            border: '1px solid rgba(16, 185, 129, 0.3)',
            boxShadow: '0 0 40px rgba(16, 185, 129, 0.15), inset 0 0 60px rgba(16, 185, 129, 0.05)'
        }}
    >
        {/* Decorative glow */}
        <div
            className="absolute -top-20 -right-20 w-60 h-60 rounded-full opacity-30 blur-3xl pointer-events-none"
            style={{ background: 'radial-gradient(circle, rgba(16, 185, 129, 0.6), transparent)' }}
        />

        <div className="relative z-10 text-center">
            {/* Icon */}
            {showIcon && Icon && (
                <div className="flex justify-center mb-4">
                    <div className="w-14 h-14 rounded-2xl bg-primary/20 border border-primary/30 flex items-center justify-center">
                        <Icon className="w-7 h-7 text-primary" />
                    </div>
                </div>
            )}

            {/* Headline */}
            <h3 className="text-2xl md:text-3xl font-bold text-white mb-3 tracking-tight">
                {headline}
            </h3>

            {/* Subtext */}
            <p className="text-white/70 text-base md:text-lg mb-6 max-w-xl mx-auto">
                {subtext}
            </p>

            {/* Button */}
            <a
                href={buttonUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-bold text-white transition-all duration-300 hover:scale-105 hover:shadow-[0_0_30px_rgba(16,185,129,0.4)]"
                style={{
                    background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                    boxShadow: '0 4px 20px rgba(16, 185, 129, 0.4)'
                }}
            >
                {buttonText}
                <ArrowRight className="w-5 h-5" />
            </a>
        </div>
    </motion.div>
);

/**
 * CTA2 - Sports/Entertainment Banner
 * Use for: Sports content, regional pages, entertainment CTAs
 * Style: Dynamic gradient with energy feel
 */
export const CTA2 = ({
    headline = "Watch NFL, NBA, MLB & All US Sports on IPTV",
    subtext = "Free 14-day trial. No credit card. Full access to all channels.",
    buttonText = "Start Sports Trial",
    buttonUrl = "https://xtreamcube.com/freetrialiptv/",
    icon: Icon = Play
}) => (
    <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative overflow-hidden rounded-2xl p-8 md:p-10"
        style={{
            background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(16, 185, 129, 0.15) 50%, rgba(6, 95, 70, 0.2) 100%)',
            border: '1px solid rgba(59, 130, 246, 0.3)',
            boxShadow: '0 0 40px rgba(59, 130, 246, 0.1)'
        }}
    >
        {/* Decorative elements */}
        <div
            className="absolute -top-10 -left-10 w-40 h-40 rounded-full opacity-20 blur-2xl pointer-events-none"
            style={{ background: 'radial-gradient(circle, rgba(59, 130, 246, 0.8), transparent)' }}
        />
        <div
            className="absolute -bottom-10 -right-10 w-40 h-40 rounded-full opacity-20 blur-2xl pointer-events-none"
            style={{ background: 'radial-gradient(circle, rgba(16, 185, 129, 0.8), transparent)' }}
        />

        <div className="relative z-10 text-center">
            {Icon && (
                <div className="flex justify-center mb-4">
                    <div className="w-12 h-12 rounded-full bg-blue-500/20 border border-blue-500/30 flex items-center justify-center">
                        <Icon className="w-6 h-6 text-blue-400" />
                    </div>
                </div>
            )}

            <h3 className="text-xl md:text-2xl font-bold text-white mb-3">
                {headline}
            </h3>

            <p className="text-white/60 text-sm md:text-base mb-6 max-w-lg mx-auto">
                {subtext}
            </p>

            <a
                href={buttonUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg font-semibold text-white transition-all duration-300 hover:scale-105"
                style={{
                    background: 'linear-gradient(135deg, #3B82F6 0%, #10B981 100%)',
                    boxShadow: '0 4px 15px rgba(59, 130, 246, 0.3)'
                }}
            >
                {buttonText}
                <ExternalLink className="w-4 h-4" />
            </a>
        </div>
    </motion.div>
);

/**
 * CTA3 - Minimal Glass Banner
 * Use for: Secondary CTAs, between content sections
 * Style: Subtle glass morphism, understated elegance
 */
export const CTA3 = ({
    headline = "Ready to Start Streaming?",
    subtext = "Join 27,500+ satisfied customers with our 24-hour free trial.",
    buttonText = "Try Free Now",
    buttonUrl = "https://xtreamcube.com/freetrialiptv/"
}) => (
    <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="rounded-xl p-6 text-center"
        style={{
            background: 'rgba(255, 255, 255, 0.03)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            backdropFilter: 'blur(10px)'
        }}
    >
        <h4 className="text-lg font-semibold text-white mb-2">{headline}</h4>
        <p className="text-white/50 text-sm mb-4">{subtext}</p>
        <a
            href={buttonUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium text-primary border border-primary/30 bg-primary/10 hover:bg-primary/20 transition-all"
        >
            {buttonText}
            <ArrowRight className="w-4 h-4" />
        </a>
    </motion.div>
);

/**
 * CTA4 - Compact Inline Banner
 * Use for: Sidebar CTAs, article intros, tight spaces
 * Style: Single-line with button aligned right
 */
export const CTA4 = ({
    text = "Start your 24-hour free trial today",
    buttonText = "Start Trial",
    buttonUrl = "https://xtreamcube.com/freetrialiptv/"
}) => (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 rounded-lg bg-primary/10 border border-primary/20">
        <p className="text-white/80 text-sm font-medium text-center sm:text-left">{text}</p>
        <a
            href={buttonUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="shrink-0 px-4 py-2 rounded-lg font-semibold text-sm text-white bg-primary hover:bg-primary/90 transition-all"
        >
            {buttonText}
        </a>
    </div>
);

/**
 * CTA5 - Value Proposition Banner
 * Use for: Pricing pages, comparison pages, bottom of articles
 * Style: Emphasizes savings/value with icons
 */
export const CTA5 = ({
    headline = "Save $2,000+ Per Year",
    features = [
        "20,000+ live channels",
        "All major sports included",
        "No contracts or hidden fees",
        "24/7 customer support"
    ],
    buttonText = "See All Channels",
    buttonUrl = "https://xtreamcube.com/freetrialiptv/"
}) => (
    <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="rounded-2xl overflow-hidden"
        style={{
            background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(0, 0, 0, 0.4) 100%)',
            border: '1px solid rgba(16, 185, 129, 0.2)'
        }}
    >
        <div className="p-6 md:p-8">
            <h3 className="text-2xl font-bold text-white mb-6 text-center">{headline}</h3>

            <div className="grid grid-cols-2 gap-3 mb-6">
                {features.map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-sm text-white/70">
                        <div className="w-5 h-5 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
                            <Star className="w-3 h-3 text-primary" />
                        </div>
                        {feature}
                    </div>
                ))}
            </div>

            <div className="text-center">
                <a
                    href={buttonUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-white transition-all hover:scale-105"
                    style={{
                        background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                        boxShadow: '0 4px 20px rgba(16, 185, 129, 0.3)'
                    }}
                >
                    {buttonText}
                    <ArrowRight className="w-5 h-5" />
                </a>
            </div>
        </div>
    </motion.div>
);

// Export all CTAs
export const CTABanners = {
    CTA1,
    CTA2,
    CTA3,
    CTA4,
    CTA5
};

export default CTABanners;
